import type { Coupon, CouponRedemption } from '@erp/shared';
import { Ticket } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { DataTable, type DataTableColumn, EmptyState } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

type SourceFilter = 'all' | 'pos_sale' | 'invoice' | 'sales_order';

interface RedemptionsResponse {
  items: CouponRedemption[];
  total: number;
  discountByCurrency: Array<{ currencyCode: string | null; amount: string }>;
}

export function CouponRedemptionsPage(): JSX.Element {
  const { t, i18n } = useTranslation('coupons');
  const [data, setData] = useState<RedemptionsResponse | null>(null);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [couponId, setCouponId] = useState('all');
  const [source, setSource] = useState<SourceFilter>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  useEffect(() => {
    void api()
      .get<{ items: Coupon[] }>('/coupons')
      .then((r) => setCoupons(r.data.items))
      .catch(() => setCoupons([]));
  }, []);

  useEffect(() => {
    const params: Record<string, string | number> = { limit: 500 };
    if (couponId !== 'all') params.couponId = couponId;
    if (source !== 'all') params.sourceType = source;
    // Send the bare date; the server anchors it to a UTC day with a half-open
    // upper bound, so the whole end day is included at microsecond precision.
    if (from) params.from = from;
    if (to) params.to = to;
    setData(null);
    void api()
      .get<RedemptionsResponse>('/coupons/redemptions', { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0, discountByCurrency: [] }));
  }, [couponId, source, from, to]);

  const couponName = (r: CouponRedemption): string => r.couponName ?? '';

  const columns: DataTableColumn<CouponRedemption>[] = useMemo(
    () => [
      {
        id: 'code',
        header: t('list.columns.code'),
        sortValue: (r) => r.couponCode,
        searchValue: (r) => `${r.couponCode} ${couponName(r)}`,
        cell: (r) => (
          <span>
            <span className="font-mono text-xs">{r.couponCode}</span>
            {r.couponName ? <span className="ms-2 text-xs text-fg-muted">{r.couponName}</span> : null}
          </span>
        ),
      },
      {
        id: 'customer',
        header: t('redemptions.columns.customer'),
        sortValue: (r) => r.partyName ?? '',
        searchValue: (r) => r.partyName ?? '',
        cell: (r) => r.partyName ?? '—',
      },
      {
        id: 'source',
        header: t('redemptions.columns.source'),
        sortValue: (r) => r.sourceType,
        searchValue: (r) => `${t(`redemptions.source.${r.sourceType}`)} ${r.sourceNumber ?? ''}`,
        cell: (r) => (
          <span className="inline-flex items-center gap-1.5">
            <Badge variant="secondary">{t(`redemptions.source.${r.sourceType}`)}</Badge>
            {r.sourceNumber ? <span className="font-mono text-xs text-fg-muted">{r.sourceNumber}</span> : null}
          </span>
        ),
      },
      {
        id: 'amount',
        header: t('redemptions.columns.amount'),
        align: 'end',
        sortValue: (r) => Number(r.discountAmount),
        cellClassName: 'font-mono tabular-nums',
        cell: (r) =>
          r.currencyCode
            ? formatMoney(r.discountAmount, { locale: i18n.language, currency: r.currencyCode })
            : r.discountAmount,
      },
      {
        id: 'date',
        header: t('redemptions.columns.date'),
        sortValue: (r) => r.redeemedAt,
        cell: (r) => formatDateTime(r.redeemedAt, { locale: i18n.language }),
      },
    ],
    [t, i18n.language],
  );

  if (!data) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('redemptions.title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('redemptions.description')}</p>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <Card className="p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
            {t('redemptions.summary.count')}
          </div>
          <div className="mt-1 text-2xl font-bold tabular-nums">{data.total}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
            {t('redemptions.summary.discount')}
          </div>
          <div className="mt-1 space-y-0.5">
            {data.discountByCurrency.length === 0 ? (
              <div className="text-2xl font-bold tabular-nums">0</div>
            ) : (
              data.discountByCurrency.map((d) => (
                <div key={d.currencyCode ?? '—'} className="text-2xl font-bold tabular-nums">
                  {d.currencyCode
                    ? formatMoney(d.amount, { locale: i18n.language, currency: d.currencyCode })
                    : d.amount}
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {data.total > data.items.length ? (
        <p className="text-xs text-warning">
          {t('redemptions.truncated', { shown: data.items.length, total: data.total })}
        </p>
      ) : null}

      {data.items.length === 0 ? (
        <EmptyState
          icon={<Ticket className="h-8 w-8" aria-hidden />}
          title={t('redemptions.empty')}
          description={t('redemptions.emptyDescription')}
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={data.items}
            rowKey={(r) => r.id}
            searchPlaceholder={t('redemptions.searchPlaceholder')}
            toolbar={
              <div className="flex flex-wrap items-center gap-1.5">
                <select
                  value={couponId}
                  onChange={(e) => setCouponId(e.target.value)}
                  aria-label={t('redemptions.filters.coupon')}
                  className="h-8 rounded-full border border-border bg-bg px-3 text-xs font-semibold text-fg-muted"
                >
                  <option value="all">{t('redemptions.filters.allCoupons')}</option>
                  {coupons.map((c) => (
                    <option key={c.id} value={c.id}>{c.code}</option>
                  ))}
                </select>
                <select
                  value={source}
                  onChange={(e) => setSource(e.target.value as SourceFilter)}
                  aria-label={t('redemptions.columns.source')}
                  className="h-8 rounded-full border border-border bg-bg px-3 text-xs font-semibold text-fg-muted"
                >
                  <option value="all">{t('redemptions.filters.allSources')}</option>
                  <option value="pos_sale">{t('redemptions.source.pos_sale')}</option>
                  <option value="invoice">{t('redemptions.source.invoice')}</option>
                  <option value="sales_order">{t('redemptions.source.sales_order')}</option>
                </select>
                <input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  aria-label={t('redemptions.filters.from')}
                  className="h-8 rounded-full border border-border bg-bg px-3 text-xs text-fg-muted"
                />
                <input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  aria-label={t('redemptions.filters.to')}
                  className="h-8 rounded-full border border-border bg-bg px-3 text-xs text-fg-muted"
                />
              </div>
            }
          />
        </Card>
      )}
    </div>
  );
}
